import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { Badge } from '../common/Badge';
import {
  backfillCalibreMovementTypesRule,
  auditCalibreDescriptions,
  suggestCalibreMovementType,
  deepVerifyCalibreMovementType,
  applyCalibreMovementTypeUpdates,
  confirmCalibreMovementType,
  getLookupCollection,
  getCustomCalibreById,
  type CustomCalibreEntry,
  type BackfillCalibreMovementTypesResult,
  type BackfillCalibreMovementTypeSample,
  type BackfillCalibreMovementTypeError,
  type AuditCalibreDescriptionsResult,
  type AuditCalibreDescriptionSample,
  type DeepVerifyCalibreMovementTypeResult,
} from '../../services/firebase';
import type { LookupValue } from '../../types';
import { useManufacturerCatalogue } from '../../hooks/useManufacturerCatalogue';
import { CustomCalibreModal } from '../CuratedWatches/CustomCalibreModal';

const MOVEMENT_LOOKUP = 'lookup_movement_types';

type Mode = 'backfill' | 'audit';

function StatPill({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: 'blue' | 'emerald' | 'amber' | 'red';
}) {
  const accentClasses =
    accent === 'blue'
      ? 'bg-blue-50 text-blue-700'
      : accent === 'emerald'
        ? 'bg-emerald-50 text-emerald-700'
        : accent === 'amber'
          ? 'bg-amber-50 text-amber-700'
          : accent === 'red'
            ? 'bg-red-50 text-red-700'
            : 'bg-gray-100 text-gray-700';
  return (
    <div className={`px-3 py-1.5 rounded-lg text-sm ${accentClasses}`}>
      <span className="font-semibold">{value}</span>
      <span className="text-xs ml-1.5 opacity-80">{label}</span>
    </div>
  );
}

/** Per-row mutable review state, keyed by calibreId (shared by both modes). */
interface RowState {
  accepted: boolean;
  aiLoading: boolean;
  aiSuggestion?: { slug: string; reasoning?: string };
  aiAccepted: boolean;
  overrideSlug?: string;
  /** "Leave as-is": stamp movementTypeReviewedAt without a type change. */
  confirmCurrent: boolean;
  /** Deep verify (web search) — in-flight + result. */
  deepLoading: boolean;
  deepResult?: DeepVerifyCalibreMovementTypeResult;
}

const emptyRow = (): RowState => ({
  accepted: false,
  aiLoading: false,
  aiAccepted: false,
  confirmCurrent: false,
  deepLoading: false,
});

/**
 * Derived value that would be written for a row if Apply is clicked.
 * Precedence: an explicit override wins, then an accepted AI suggestion, then the
 * accepted rule proposal. Empty when the row hasn't been actioned.
 */
function pendingSlug(row: RowState, ruleProposal: string): string {
  if (row.overrideSlug) return row.overrideSlug;
  if (row.aiAccepted && row.aiSuggestion?.slug) return row.aiSuggestion.slug;
  if (row.accepted) return ruleProposal;
  return '';
}

/** The slug a deep-verify result recommends adopting, or null if no change. */
function deepSuggestedSlug(d: DeepVerifyCalibreMovementTypeResult): string | null {
  if (d.recommendation === 'web_correct' && d.webFinding && d.webFinding !== 'unknown')
    return d.webFinding;
  if (d.recommendation === 'description_correct' && d.descriptionFinding)
    return d.descriptionFinding;
  return null;
}

const RECOMMENDATION_LABEL: Record<DeepVerifyCalibreMovementTypeResult['recommendation'], string> = {
  current_correct: 'Current type correct',
  web_correct: 'Web sources contradict current',
  description_correct: 'Description correct, current wrong',
  ambiguous: 'Ambiguous — sources unclear',
};

/** A unified, apply-relevant view of a row from either mode's result. */
interface ActionableSample {
  calibreId: string;
  label: string;
  oldType: string;
  /** The rule proposal to accept (backfill mode only; '' in audit mode). */
  ruleProposal: string;
}

interface Props {
  /** Switches the dashboard to the Lookup Collections tab (to add a new type). */
  onOpenLookupManager?: () => void;
  /**
   * @deprecated The page now opens the Calibre Editor as a local modal overlay
   * (see `editingCalibreId`) so pending per-row review state survives editing.
   * Retained for backward compatibility with the DashboardPage tab deep-link,
   * which is left wired but no longer used by this page.
   */
  onOpenCalibre?: (calibreId: string) => void;
}

/**
 * Catalogue → "Calibre Movement-Type Backfill".
 *
 * Two modes on one page:
 *
 *  - Type Backfill (default): human-in-the-loop re-tagging of `data.movementType`.
 *    Run the deterministic rule-match pass (name + aliases) to PROPOSE upgrades and
 *    flag conflicts; per row accept it, ask AI, deep-verify against the web,
 *    override, or skip; then apply only the rows you actioned.
 *
 *  - Description Audit: diagnoses calibres whose DESCRIPTION text disagrees with
 *    the stored type (the failure mode where a bad description misleads AI). Runs
 *    the rule match against the description ONLY and surfaces contradictions. Fix
 *    the description via "Open in Calibre Editor", override the type, or confirm
 *    the current value (which also marks the description reviewed).
 *
 * Nothing is written until you click Apply.
 */
export function CalibreMovementTypeBackfill({ onOpenLookupManager }: Props) {
  const [mode, setMode] = useState<Mode>('backfill');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<BackfillCalibreMovementTypesResult | null>(null);
  const [auditResult, setAuditResult] = useState<AuditCalibreDescriptionsResult | null>(null);
  const [hideMatches, setHideMatches] = useState(true);
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [errorsExpanded, setErrorsExpanded] = useState(false);

  // Scan controls.
  const [manufacturerId, setManufacturerId] = useState('');
  const [includeAll, setIncludeAll] = useState(false);
  const [includeReviewed, setIncludeReviewed] = useState(false);

  // Movement-type options for the override dropdown.
  const [lookupValues, setLookupValues] = useState<LookupValue[]>([]);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Apply flow.
  const [applyConfirm, setApplyConfirm] = useState(false);
  const [applying, setApplying] = useState(false);

  // Inline Calibre Editor — opened as a modal OVERLAY on this page (not a tab
  // switch) so the table's pending overrides/checkboxes survive editing.
  // `openingCalibreId` is the row whose entry is being fetched; `editingEntry`
  // is the loaded entry the modal edits.
  const [editingEntry, setEditingEntry] = useState<CustomCalibreEntry | null>(null);
  const [openingCalibreId, setOpeningCalibreId] = useState<string | null>(null);

  const openEditor = async (calibreId: string) => {
    setOpeningCalibreId(calibreId);
    try {
      const entry = await getCustomCalibreById(calibreId);
      if (!entry) {
        toast.error(`Calibre ${calibreId} not found`);
        return;
      }
      setEditingEntry(entry);
    } catch (err) {
      console.error('Failed to load calibre for editing:', err);
      toast.error(
        err instanceof Error ? `Could not open editor: ${err.message}` : 'Could not open editor',
      );
    } finally {
      setOpeningCalibreId(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLookupLoading(true);
    getLookupCollection(MOVEMENT_LOOKUP)
      .then((values) => {
        if (!cancelled) setLookupValues(values);
      })
      .catch((err) => {
        console.error('Failed to load lookup_movement_types:', err);
        if (!cancelled) toast.error('Could not load movement-type options for overrides');
      })
      .finally(() => {
        if (!cancelled) setLookupLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Manufacturer filter options (name-sorted by the catalogue, which reproduces
  // the `orderBy('name')` this used to get from Firestore — the dropdown below
  // renders the list as-is and never re-sorts). Through the shared hook, so one
  // created elsewhere in the app shows up here without a page reload.
  const { manufacturers } = useManufacturerCatalogue();

  const slugOptions = useMemo(
    () =>
      lookupValues
        .filter((v) => v.isActive !== false && v.value)
        .map((v) => ({ slug: v.value, label: v.displayName || v.value }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [lookupValues],
  );

  const hasResult = mode === 'backfill' ? !!result : !!auditResult;

  const runRule = async (overrides?: {
    manufacturerId?: string;
    includeAll?: boolean;
    includeReviewed?: boolean;
  }) => {
    const opts = {
      manufacturerId: overrides?.manufacturerId ?? manufacturerId,
      includeAll: overrides?.includeAll ?? includeAll,
      includeReviewed: overrides?.includeReviewed ?? includeReviewed,
    };
    setRunning(true);
    try {
      const r = await backfillCalibreMovementTypesRule({
        includeAll: opts.includeAll,
        includeReviewed: opts.includeReviewed,
        manufacturerId: opts.manufacturerId || undefined,
      });
      setResult(r);
      // Fresh review state — one row per proposed sample.
      const next: Record<string, RowState> = {};
      for (const s of r.samples) next[s.calibreId] = emptyRow();
      setRows(next);
      const summary =
        `Rule pass complete: ${r.scanned} scanned, ${r.updated} proposed upgrade(s), ` +
        `${r.ruleMatched} matched` +
        (r.conflicts ? `, ${r.conflicts} conflict(s)` : '') +
        (r.reviewedSkipped ? `, ${r.reviewedSkipped} reviewed-skipped` : '') +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length || r.conflicts) toast(summary, { icon: '⚠️' });
      else toast.success(summary);
    } catch (err) {
      console.error('backfillCalibreMovementTypesRule failed:', err);
      toast.error(
        err instanceof Error ? `Rule pass failed: ${err.message}` : 'Rule pass failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const runAudit = async (overrides?: {
    manufacturerId?: string;
    includeReviewed?: boolean;
  }) => {
    const opts = {
      manufacturerId: overrides?.manufacturerId ?? manufacturerId,
      includeReviewed: overrides?.includeReviewed ?? includeReviewed,
    };
    setRunning(true);
    try {
      const r = await auditCalibreDescriptions({
        includeReviewed: opts.includeReviewed,
        manufacturerId: opts.manufacturerId || undefined,
      });
      setAuditResult(r);
      const next: Record<string, RowState> = {};
      for (const s of r.samples) next[s.calibreId] = emptyRow();
      setRows(next);
      const summary =
        `Audit complete: ${r.scanned} scanned, ${r.contradictions} contradiction(s), ` +
        `${r.matches} match(es), ${r.noMention} no-mention` +
        (r.reviewedSkipped ? `, ${r.reviewedSkipped} reviewed-skipped` : '') +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length || r.contradictions) toast(summary, { icon: '⚠️' });
      else toast.success(summary);
    } catch (err) {
      console.error('auditCalibreDescriptions failed:', err);
      toast.error(
        err instanceof Error ? `Audit failed: ${err.message}` : 'Audit failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  // Re-run the active mode's pass with the given control overrides.
  const rerun = (overrides?: {
    manufacturerId?: string;
    includeAll?: boolean;
    includeReviewed?: boolean;
  }) => (mode === 'backfill' ? runRule(overrides) : runAudit(overrides));

  const onModeChange = (m: Mode) => {
    if (m === mode) return;
    setMode(m);
    // Clear the table — the two passes are unrelated; force an explicit re-run.
    setResult(null);
    setAuditResult(null);
    setRows({});
    setErrorsExpanded(false);
  };

  // Control handlers — update state, and re-run immediately if a pass has already
  // been run (so the table reflects the new filter without a manual click).
  const onManufacturerChange = (id: string) => {
    setManufacturerId(id);
    if (hasResult && !running) rerun({ manufacturerId: id });
  };
  const onIncludeAllChange = (v: boolean) => {
    setIncludeAll(v);
    if (result && !running && mode === 'backfill') runRule({ includeAll: v });
  };
  const onIncludeReviewedChange = (v: boolean) => {
    setIncludeReviewed(v);
    if (hasResult && !running) rerun({ includeReviewed: v });
  };

  const patchRow = (calibreId: string, patch: Partial<RowState>) =>
    setRows((prev) => ({
      ...prev,
      [calibreId]: { ...(prev[calibreId] ?? emptyRow()), ...patch },
    }));

  const askAi = async (calibreId: string) => {
    patchRow(calibreId, { aiLoading: true });
    try {
      const r = await suggestCalibreMovementType(calibreId);
      if (!r.suggestedSlug) {
        patchRow(calibreId, {
          aiLoading: false,
          aiSuggestion: { slug: '', reasoning: r.reasoning },
        });
        toast('AI had no confident suggestion for this calibre', { icon: '🤔' });
        return;
      }
      patchRow(calibreId, {
        aiLoading: false,
        aiSuggestion: { slug: r.suggestedSlug, reasoning: r.reasoning },
      });
    } catch (err) {
      console.error('suggestCalibreMovementType failed:', err);
      patchRow(calibreId, { aiLoading: false });
      toast.error(
        err instanceof Error ? `AI suggestion failed: ${err.message}` : 'AI suggestion failed',
      );
    }
  };

  const deepVerify = async (calibreId: string) => {
    patchRow(calibreId, { deepLoading: true });
    try {
      const r = await deepVerifyCalibreMovementType(calibreId);
      patchRow(calibreId, { deepLoading: false, deepResult: r });
    } catch (err) {
      console.error('deepVerifyCalibreMovementType failed:', err);
      patchRow(calibreId, { deepLoading: false });
      toast.error(
        err instanceof Error ? `Deep verify failed: ${err.message}` : 'Deep verify failed',
      );
    }
  };

  // The apply-relevant view of the active result's samples.
  const actionableSamples = useMemo<ActionableSample[]>(() => {
    if (mode === 'backfill') {
      if (!result) return [];
      return result.samples.map((s) => ({
        calibreId: s.calibreId,
        label: s.calibreName || s.calibreId,
        oldType: s.oldType,
        ruleProposal: s.newType,
      }));
    }
    if (!auditResult) return [];
    return auditResult.samples.map((s) => ({
      calibreId: s.calibreId,
      label: s.name || s.calibreId,
      oldType: s.currentType,
      ruleProposal: '',
    }));
  }, [mode, result, auditResult]);

  // Collect every actioned row into a concrete update list. A row with both a
  // pending type change AND confirmCurrent is treated as an update (the type
  // change wins) — see pendingConfirms.
  const pendingUpdates = useMemo(() => {
    const byId = new Map(actionableSamples.map((s) => [s.calibreId, s]));
    const out: { calibreId: string; calibreName: string; oldType: string; newSlug: string }[] = [];
    for (const [calibreId, row] of Object.entries(rows)) {
      const sample = byId.get(calibreId);
      if (!sample) continue;
      const slug = pendingSlug(row, sample.ruleProposal);
      if (slug) {
        out.push({ calibreId, calibreName: sample.label, oldType: sample.oldType, newSlug: slug });
      }
    }
    return out;
  }, [rows, actionableSamples]);

  // Rows marked "Confirm current" with NO pending type change — these only get a
  // reviewed stamp (no type change, no Algolia reindex).
  const pendingConfirms = useMemo(() => {
    const byId = new Map(actionableSamples.map((s) => [s.calibreId, s]));
    const out: { calibreId: string; calibreName: string; oldType: string }[] = [];
    for (const [calibreId, row] of Object.entries(rows)) {
      if (!row.confirmCurrent) continue;
      const sample = byId.get(calibreId);
      if (!sample) continue;
      if (pendingSlug(row, sample.ruleProposal)) continue;
      out.push({ calibreId, calibreName: sample.label, oldType: sample.oldType });
    }
    return out;
  }, [rows, actionableSamples]);

  const totalActions = pendingUpdates.length + pendingConfirms.length;

  const onApply = () => {
    if (totalActions === 0) {
      toast('Nothing to apply — override, accept a suggestion, or confirm a row first', {
        icon: 'ℹ️',
      });
      return;
    }
    setApplyConfirm(true);
  };

  const doApply = async () => {
    setApplyConfirm(false);
    setApplying(true);
    try {
      let appliedCount = 0;
      let confirmedCount = 0;
      let failedCount = 0;

      if (pendingUpdates.length > 0) {
        const res = await applyCalibreMovementTypeUpdates(
          pendingUpdates.map((u) => ({ calibreId: u.calibreId, newSlug: u.newSlug })),
        );
        appliedCount = res.applied;
        failedCount += res.failed.length;
        if (res.failed.length) console.warn('applyCalibreMovementTypeUpdates failures:', res.failed);
      }

      if (pendingConfirms.length > 0) {
        // In Description Audit mode, confirming also stamps descriptionReviewedAt
        // so the audit stops re-flagging the row.
        const res = await confirmCalibreMovementType(
          pendingConfirms.map((c) => c.calibreId),
          { markDescriptionReviewed: mode === 'audit' },
        );
        confirmedCount = res.confirmed;
        failedCount += res.failed.length;
        if (res.failed.length) console.warn('confirmCalibreMovementType failures:', res.failed);
      }

      const parts: string[] = [];
      if (appliedCount) parts.push(`${appliedCount} update(s)`);
      if (confirmedCount) parts.push(`${confirmedCount} confirm(s)`);
      const done = parts.join(' + ') || 'nothing';
      if (failedCount) {
        toast(`Applied ${done}, ${failedCount} failed — see console`, { icon: '⚠️' });
      } else {
        toast.success(`Applied ${done}${appliedCount ? ' — re-indexing to Algolia' : ''}`);
      }
      // Re-run the active pass so the table reflects what's now written/reviewed.
      await rerun();
    } catch (err) {
      console.error('apply/confirm failed:', err);
      toast.error(err instanceof Error ? `Apply failed: ${err.message}` : 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const applyLabel = (() => {
    const parts: string[] = [];
    if (pendingUpdates.length)
      parts.push(`${pendingUpdates.length} update${pendingUpdates.length === 1 ? '' : 's'}`);
    if (pendingConfirms.length)
      parts.push(`${pendingConfirms.length} confirm${pendingConfirms.length === 1 ? '' : 's'}`);
    return parts.length ? `Apply ${parts.join(' + ')}` : 'Apply';
  })();

  // Shared inline panel under a row showing a deep-verify result + accept-as-override.
  const renderDeepPanel = (calibreId: string, row: RowState, currentType: string) => {
    if (!row.deepResult) return null;
    const d = row.deepResult;
    const suggested = deepSuggestedSlug(d);
    const canAccept = !!suggested && suggested !== currentType && row.overrideSlug !== suggested;
    return (
      <div className="mt-2 rounded-md border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-xs space-y-1">
        <div>
          <span className="text-gray-500">Web says:</span>{' '}
          <span className="font-mono text-indigo-700">{d.webFinding ?? 'unknown'}</span>
          {d.webSources.length > 0 && (
            <span className="ml-1 text-gray-500">
              (
              {d.webSources.map((u, i) => (
                <span key={u}>
                  {i > 0 && ', '}
                  <a
                    href={u}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 underline hover:text-indigo-800"
                  >
                    source {i + 1}
                  </a>
                </span>
              ))}
              )
            </span>
          )}
        </div>
        <div>
          <span className="text-gray-500">Description says:</span>{' '}
          <span className="font-mono text-gray-700">{d.descriptionFinding ?? '—'}</span>
        </div>
        <div>
          <span className="text-gray-500">Current:</span>{' '}
          <span className="font-mono text-gray-700">{currentType || '(empty)'}</span>
        </div>
        <div>
          <span className="text-gray-500">Recommendation:</span>{' '}
          <span className="font-medium text-gray-800">{RECOMMENDATION_LABEL[d.recommendation]}</span>
          {d.reasoning && <span className="text-gray-500"> — {d.reasoning}</span>}
        </div>
        {canAccept && (
          <button
            type="button"
            onClick={() => patchRow(calibreId, { overrideSlug: suggested! })}
            className="mt-1 px-2 py-0.5 rounded bg-indigo-600 text-white font-medium hover:bg-indigo-700"
          >
            Accept <span className="font-mono">{suggested}</span> as override
          </button>
        )}
      </div>
    );
  };

  const deepVerifyButton = (calibreId: string, row: RowState) => (
    <Button
      variant="ghost"
      size="sm"
      loading={row.deepLoading}
      disabled={row.deepLoading}
      onClick={() => deepVerify(calibreId)}
      title="Cross-check against authoritative web sources (slow — uses web search)"
    >
      <svg className="w-4 h-4 mr-1" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
          clipRule="evenodd"
        />
      </svg>
      Deep verify
    </Button>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Calibre Movement-Type Backfill</h1>
        <p className="text-gray-500 mt-1 max-w-3xl">
          Re-tags{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">data.movementType</code> on
          custom calibres. Use <span className="font-medium">Type Backfill</span> to propose and
          apply type upgrades, or <span className="font-medium">Description Audit</span> to find
          calibres whose description text disagrees with their stored type. Nothing is written until
          you click <span className="font-medium">Apply</span>.
        </p>
      </div>

      {/* Mode toggle */}
      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 text-sm">
        <button
          type="button"
          onClick={() => onModeChange('backfill')}
          disabled={running || applying}
          className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
            mode === 'backfill' ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          Type Backfill
        </button>
        <button
          type="button"
          onClick={() => onModeChange('audit')}
          disabled={running || applying}
          className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
            mode === 'audit' ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          Description Audit
        </button>
      </div>

      <div className="bg-white shadow rounded-lg p-4 space-y-4">
        {mode === 'backfill' ? (
          <p className="text-sm text-gray-600 max-w-3xl">
            The rule pass scans each calibre&apos;s{' '}
            <span className="font-medium">name and aliases</span> (not the description — too noisy)
            against{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">lookup_movement_types</code>.
            It only proposes; it never writes. Only empty or generic values are proposed as upgrades;
            a conflicting existing specific value is flagged in amber.
          </p>
        ) : (
          <p className="text-sm text-gray-600 max-w-3xl">
            The audit runs the same rule match against each calibre&apos;s{' '}
            <span className="font-medium">description ONLY</span> and compares it to the stored type.
            A <span className="text-red-700 font-medium">contradiction</span> means the description
            implies a different type than is stored (the description is likely wrong — or the type
            is). Fix the description via <span className="font-medium">Open in Calibre Editor</span>,
            override the type, or confirm the current value (which also marks the description
            reviewed). It never writes and never calls AI.
          </p>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="primary"
            loading={running}
            disabled={running || applying}
            onClick={() => rerun()}
          >
            {mode === 'backfill'
              ? result
                ? 'Re-run rule match'
                : 'Run rule match'
              : auditResult
                ? 'Re-run audit'
                : 'Run description audit'}
          </Button>

          {/* Manufacturer filter — changing it re-runs the scan. */}
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-gray-500">Manufacturer</span>
            <select
              value={manufacturerId}
              disabled={running || applying}
              onChange={(e) => onManufacturerChange(e.target.value)}
              className="text-sm border-gray-300 rounded focus:ring-brand-500 focus:border-brand-500 max-w-[14rem]"
            >
              <option value="">All manufacturers</option>
              {manufacturers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>

          {mode === 'backfill' && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={includeAll}
                disabled={running || applying}
                onChange={(e) => onIncludeAllChange(e.target.checked)}
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              Show all calibres
            </label>
          )}

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={includeReviewed}
              disabled={running || applying}
              onChange={(e) => onIncludeReviewedChange(e.target.checked)}
              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            Include already-reviewed
          </label>

          {mode === 'audit' && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={hideMatches}
                disabled={running || applying}
                onChange={(e) => setHideMatches(e.target.checked)}
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              Hide matches (show contradictions only)
            </label>
          )}

          {onOpenLookupManager && (
            <button
              type="button"
              onClick={onOpenLookupManager}
              className="text-sm text-brand-600 hover:text-brand-700 underline"
            >
              Add a new movement type →
            </button>
          )}
        </div>
        {mode === 'backfill' ? (
          <p className="text-xs text-gray-400 max-w-3xl">
            <span className="font-medium">Show all calibres</span> surfaces every calibre (not just
            rule-flagged ones) so you can spot-check by manufacturer — rows with no rule match show
            an empty proposal. <span className="font-medium">Deep verify</span> cross-checks a row
            against authoritative web sources (slow). There is no un-review button — to revisit a
            prior decision, tick <span className="font-medium">Include already-reviewed</span> and
            re-action the row.
          </p>
        ) : (
          <p className="text-xs text-gray-400 max-w-3xl">
            Review state here is tracked by an independent{' '}
            <code className="text-[11px] bg-gray-100 px-1 py-0.5 rounded">descriptionReviewedAt</code>{' '}
            field, so confirming a description as reviewed does not affect the Type Backfill pass and
            vice-versa.
          </p>
        )}
      </div>

      {/* ---- Type Backfill results ---- */}
      {mode === 'backfill' && result && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <StatPill label="Scanned" value={result.scanned} />
            <StatPill label="Proposed upgrades" value={result.updated} accent="emerald" />
            <StatPill label="Rule matched" value={result.ruleMatched} accent="blue" />
            <StatPill label="Unchanged" value={result.unchanged} />
            <StatPill label="Conflicts" value={result.conflicts} accent="amber" />
            <StatPill label="No match" value={result.noMatch} />
            {result.reviewedSkipped > 0 && (
              <StatPill label="Reviewed-skipped" value={result.reviewedSkipped} />
            )}
            {result.errors.length > 0 && (
              <StatPill label="Errors" value={result.errors.length} accent="red" />
            )}
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Proposed changes</h3>
              <span className="text-xs text-gray-500">
                {result.samples.length === 0 ? 'none' : `${result.samples.length} row(s)`}
              </span>
            </div>
            {result.samples.length === 0 ? (
              <div className="px-4 py-6 text-sm text-gray-500">
                No upgrades or conflicts to show. Every scanned calibre either already had the right
                type or had no confident match.
              </div>
            ) : (
              <div className="max-h-[36rem] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 text-gray-500 z-10">
                    <tr>
                      <th className="text-left font-medium px-3 py-2 w-10">Apply</th>
                      <th className="text-left font-medium px-3 py-2">Calibre</th>
                      <th className="text-left font-medium px-3 py-2">Current</th>
                      <th className="text-left font-medium px-3 py-2">Rule proposal</th>
                      <th className="text-left font-medium px-3 py-2">AI</th>
                      <th className="text-left font-medium px-3 py-2">Override</th>
                      <th className="text-left font-medium px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {result.samples.map((s: BackfillCalibreMovementTypeSample) => {
                      const row = rows[s.calibreId] ?? emptyRow();
                      const willWrite = pendingSlug(row, s.newType);
                      return (
                        <tr key={s.calibreId} className={s.conflict ? 'bg-amber-50' : undefined}>
                          {/* Apply checkbox */}
                          <td className="px-3 py-2 align-top">
                            <input
                              type="checkbox"
                              checked={row.accepted}
                              onChange={(e) => patchRow(s.calibreId, { accepted: e.target.checked })}
                              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 mt-0.5"
                            />
                          </td>

                          {/* Calibre */}
                          <td className="px-3 py-2 text-gray-800 align-top">
                            <div>{s.calibreName || s.calibreId}</div>
                            {s.conflict && (
                              <Badge variant="warning">conflict — overwrites a specific value</Badge>
                            )}
                            {s.reviewed && <Badge variant="default">already reviewed</Badge>}
                          </td>

                          {/* Current */}
                          <td className="px-3 py-2 font-mono text-gray-600 align-top">{s.oldType}</td>

                          {/* Rule proposal */}
                          <td className="px-3 py-2 font-mono text-gray-900 align-top">{s.newType}</td>

                          {/* AI + Deep verify */}
                          <td className="px-3 py-2 align-top">
                            <div className="flex flex-col items-start gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                loading={row.aiLoading}
                                disabled={row.aiLoading}
                                onClick={() => askAi(s.calibreId)}
                              >
                                <svg
                                  className="w-4 h-4 mr-1"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  aria-hidden="true"
                                >
                                  <path d="M11 3a1 1 0 10-2 0v1.586l-.293-.293a1 1 0 10-1.414 1.414L9 7.414V9H7.414L5.707 7.293a1 1 0 10-1.414 1.414L4.586 9H3a1 1 0 100 2h1.586l-.293.293a1 1 0 101.414 1.414L7.414 11H9v1.586l-1.707 1.707a1 1 0 101.414 1.414L9 15.414V17a1 1 0 102 0v-1.586l.293.293a1 1 0 001.414-1.414L11 12.586V11h1.586l1.707 1.707a1 1 0 001.414-1.414L15.414 11H17a1 1 0 100-2h-1.586l.293-.293a1 1 0 00-1.414-1.414L12.586 9H11V7.414l1.707-1.707a1 1 0 00-1.414-1.414L11 4.586V3z" />
                                </svg>
                                Ask AI
                              </Button>
                              {deepVerifyButton(s.calibreId, row)}
                            </div>
                            {row.aiSuggestion && (
                              <div className="mt-1.5 text-xs">
                                {row.aiSuggestion.slug ? (
                                  <div className="flex items-start gap-2">
                                    <span className="text-gray-700">
                                      AI suggests:{' '}
                                      <span className="font-mono text-blue-700">
                                        {row.aiSuggestion.slug}
                                      </span>
                                      {row.aiSuggestion.reasoning && (
                                        <span className="text-gray-500">
                                          {' '}
                                          (&ldquo;{row.aiSuggestion.reasoning}&rdquo;)
                                        </span>
                                      )}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        patchRow(s.calibreId, { aiAccepted: !row.aiAccepted })
                                      }
                                      className={`shrink-0 px-2 py-0.5 rounded text-xs font-medium ${
                                        row.aiAccepted
                                          ? 'bg-blue-600 text-white'
                                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                      }`}
                                    >
                                      {row.aiAccepted ? 'Accepted ✓' : 'Accept'}
                                    </button>
                                  </div>
                                ) : (
                                  <span className="text-gray-500 italic">
                                    AI: no confident suggestion
                                    {row.aiSuggestion.reasoning
                                      ? ` (${row.aiSuggestion.reasoning})`
                                      : ''}
                                  </span>
                                )}
                              </div>
                            )}
                            {renderDeepPanel(s.calibreId, row, s.oldType)}
                          </td>

                          {/* Override */}
                          <td className="px-3 py-2 align-top">
                            <select
                              value={row.overrideSlug ?? ''}
                              disabled={lookupLoading}
                              onChange={(e) =>
                                patchRow(s.calibreId, { overrideSlug: e.target.value || undefined })
                              }
                              className="text-xs border-gray-300 rounded focus:ring-brand-500 focus:border-brand-500 max-w-[10rem]"
                            >
                              <option value="">— none —</option>
                              {slugOptions.map((o) => (
                                <option key={o.slug} value={o.slug}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </td>

                          {/* Action — a pending type change wins; otherwise offer
                              "Confirm current" (reviewed-only stamp, no type change). */}
                          <td className="px-3 py-2 align-top">
                            {willWrite ? (
                              <span className="font-mono text-emerald-700">{willWrite}</span>
                            ) : row.confirmCurrent ? (
                              <button
                                type="button"
                                onClick={() => patchRow(s.calibreId, { confirmCurrent: false })}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-600 text-white"
                                title="Click to un-mark"
                              >
                                Will confirm ✓
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => patchRow(s.calibreId, { confirmCurrent: true })}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-gray-600 hover:bg-gray-100 border border-gray-200"
                                title="Mark this calibre reviewed without changing its type"
                              >
                                <svg
                                  className="w-3.5 h-3.5"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  aria-hidden="true"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                Confirm current
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {result.samples.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  {totalActions === 0
                    ? 'No rows actioned'
                    : `${pendingUpdates.length} update(s), ${pendingConfirms.length} confirm(s) ready`}
                </span>
                <Button
                  variant="primary"
                  loading={applying}
                  disabled={applying || running || totalActions === 0}
                  onClick={onApply}
                >
                  {applyLabel}
                </Button>
              </div>
            )}
          </div>

          {result.errors.length > 0 && renderErrors(result.errors)}
        </div>
      )}

      {/* ---- Description Audit results ---- */}
      {mode === 'audit' && auditResult && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <StatPill label="Scanned" value={auditResult.scanned} />
            <StatPill label="Contradictions" value={auditResult.contradictions} accent="red" />
            <StatPill label="Matches" value={auditResult.matches} accent="emerald" />
            <StatPill label="No mention" value={auditResult.noMention} />
            {auditResult.reviewedSkipped > 0 && (
              <StatPill label="Reviewed-skipped" value={auditResult.reviewedSkipped} />
            )}
            {auditResult.errors.length > 0 && (
              <StatPill label="Errors" value={auditResult.errors.length} accent="red" />
            )}
          </div>

          {(() => {
            const visible = auditResult.samples.filter(
              (s) => !(hideMatches && !s.contradictsCurrent),
            );
            return (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">Description vs. stored type</h3>
                  <span className="text-xs text-gray-500">
                    {visible.length === 0 ? 'none' : `${visible.length} row(s)`}
                  </span>
                </div>
                {visible.length === 0 ? (
                  <div className="px-4 py-6 text-sm text-gray-500">
                    No {hideMatches ? 'contradictions' : 'rows'} to show. Every scanned
                    description either confirmed the stored type or mentioned no movement type.
                  </div>
                ) : (
                  <div className="max-h-[36rem] overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 text-gray-500 z-10">
                        <tr>
                          <th className="text-left font-medium px-3 py-2">Calibre</th>
                          <th className="text-left font-medium px-3 py-2">Current type</th>
                          <th className="text-left font-medium px-3 py-2">Description says</th>
                          <th className="text-left font-medium px-3 py-2">Status</th>
                          <th className="text-left font-medium px-3 py-2">Override</th>
                          <th className="text-left font-medium px-3 py-2">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {visible.map((s: AuditCalibreDescriptionSample) => {
                          const row = rows[s.calibreId] ?? emptyRow();
                          const willWrite = pendingSlug(row, '');
                          return (
                            <tr
                              key={s.calibreId}
                              className={s.contradictsCurrent ? 'bg-red-50' : undefined}
                            >
                              {/* Calibre */}
                              <td className="px-3 py-2 text-gray-800 align-top">
                                <div>{s.name || s.calibreId}</div>
                                <div className="mt-1 flex flex-wrap items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => openEditor(s.calibreId)}
                                    disabled={openingCalibreId === s.calibreId}
                                    className="text-xs text-brand-600 hover:text-brand-700 underline disabled:opacity-50"
                                  >
                                    {openingCalibreId === s.calibreId
                                      ? 'Opening…'
                                      : 'Open in Calibre Editor →'}
                                  </button>
                                </div>
                                {s.reviewed && <Badge variant="default">already reviewed</Badge>}
                                {deepVerifyButton(s.calibreId, row)}
                                {renderDeepPanel(s.calibreId, row, s.currentType)}
                              </td>

                              {/* Current type */}
                              <td className="px-3 py-2 font-mono text-gray-600 align-top">
                                {s.currentType || '(empty)'}
                              </td>

                              {/* Description says */}
                              <td className="px-3 py-2 font-mono text-gray-900 align-top">
                                {s.descriptionSays}
                              </td>

                              {/* Status */}
                              <td className="px-3 py-2 align-top">
                                {s.contradictsCurrent ? (
                                  <Badge variant="danger">contradiction</Badge>
                                ) : (
                                  <Badge variant="success">match</Badge>
                                )}
                              </td>

                              {/* Override */}
                              <td className="px-3 py-2 align-top">
                                <select
                                  value={row.overrideSlug ?? ''}
                                  disabled={lookupLoading}
                                  onChange={(e) =>
                                    patchRow(s.calibreId, {
                                      overrideSlug: e.target.value || undefined,
                                    })
                                  }
                                  className="text-xs border-gray-300 rounded focus:ring-brand-500 focus:border-brand-500 max-w-[10rem]"
                                >
                                  <option value="">— none —</option>
                                  {slugOptions.map((o) => (
                                    <option key={o.slug} value={o.slug}>
                                      {o.label}
                                    </option>
                                  ))}
                                </select>
                              </td>

                              {/* Action */}
                              <td className="px-3 py-2 align-top">
                                {willWrite ? (
                                  <span className="font-mono text-emerald-700">{willWrite}</span>
                                ) : row.confirmCurrent ? (
                                  <button
                                    type="button"
                                    onClick={() => patchRow(s.calibreId, { confirmCurrent: false })}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-600 text-white"
                                    title="Click to un-mark"
                                  >
                                    Will confirm ✓
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => patchRow(s.calibreId, { confirmCurrent: true })}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-gray-600 hover:bg-gray-100 border border-gray-200"
                                    title="Mark this calibre's type AND description reviewed without changing the type"
                                  >
                                    <svg
                                      className="w-3.5 h-3.5"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                      aria-hidden="true"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    Confirm current
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {visible.length > 0 && (
                  <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {totalActions === 0
                        ? 'No rows actioned'
                        : `${pendingUpdates.length} update(s), ${pendingConfirms.length} confirm(s) ready`}
                    </span>
                    <Button
                      variant="primary"
                      loading={applying}
                      disabled={applying || running || totalActions === 0}
                      onClick={onApply}
                    >
                      {applyLabel}
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}

          {auditResult.errors.length > 0 && renderErrors(auditResult.errors)}
        </div>
      )}

      {/* Apply confirm — shows the exact list of writes and confirm-only stamps. */}
      <Modal isOpen={applyConfirm} onClose={() => setApplyConfirm(false)} title={`${applyLabel}?`}>
        <div>
          <p className="text-sm text-gray-700">
            {pendingUpdates.length > 0 && (
              <>
                Writes{' '}
                <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">data.movementType</code>{' '}
                on the {pendingUpdates.length} update row(s) below and re-indexes them to Algolia.{' '}
              </>
            )}
            {pendingConfirms.length > 0 && (
              <>
                Marks {pendingConfirms.length} row(s) reviewed (no type change, no reindex)
                {mode === 'audit' ? ', stamping both the type and description as reviewed' : ''} so
                they stop re-appearing.{' '}
              </>
            )}
            This cannot be undone in bulk.
          </p>

          {pendingUpdates.length > 0 && (
            <div className="mt-3 max-h-60 overflow-auto border border-gray-100 rounded-lg">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50 text-gray-500">
                  <tr>
                    <th className="text-left font-medium px-3 py-1.5">Calibre (update)</th>
                    <th className="text-left font-medium px-3 py-1.5">From</th>
                    <th className="text-left font-medium px-3 py-1.5">To</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pendingUpdates.map((u) => (
                    <tr key={u.calibreId}>
                      <td className="px-3 py-1.5 text-gray-800 align-top">{u.calibreName}</td>
                      <td className="px-3 py-1.5 font-mono text-gray-500 align-top">{u.oldType}</td>
                      <td className="px-3 py-1.5 font-mono text-emerald-700 align-top">{u.newSlug}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pendingConfirms.length > 0 && (
            <div className="mt-3 max-h-60 overflow-auto border border-gray-100 rounded-lg">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50 text-gray-500">
                  <tr>
                    <th className="text-left font-medium px-3 py-1.5">Calibre (confirm current)</th>
                    <th className="text-left font-medium px-3 py-1.5">Keeps</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pendingConfirms.map((c) => (
                    <tr key={c.calibreId}>
                      <td className="px-3 py-1.5 text-gray-800 align-top">{c.calibreName}</td>
                      <td className="px-3 py-1.5 font-mono text-gray-500 align-top">{c.oldType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setApplyConfirm(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={doApply}>
              {applyLabel}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Inline Calibre Editor overlay. Mounting it here (rather than navigating
          to the Curated Calibres tab) keeps this page — and all its pending
          per-row review state — alive underneath. On save we re-run the active
          pass so the edited row reflects the freshly-written description/type. */}
      {editingEntry && (
        <CustomCalibreModal
          isOpen
          initialEditEntry={editingEntry}
          onClose={() => setEditingEntry(null)}
          onSaved={() => {
            setEditingEntry(null);
            void rerun();
          }}
        />
      )}
    </div>
  );

  // Collapsible error panel, shared by both modes.
  function renderErrors(errors: BackfillCalibreMovementTypeError[]) {
    return (
      <div className="border border-red-200 rounded-lg bg-white">
        <button
          type="button"
          onClick={() => setErrorsExpanded((v) => !v)}
          className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg"
        >
          <svg
            className={`w-4 h-4 transition-transform ${errorsExpanded ? 'rotate-90' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clipRule="evenodd"
            />
          </svg>
          {errors.length} error{errors.length === 1 ? '' : 's'} — click to{' '}
          {errorsExpanded ? 'collapse' : 'expand'}
        </button>
        {errorsExpanded && (
          <div className="border-t border-red-100 max-h-72 overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left font-medium px-4 py-1.5">Calibre</th>
                  <th className="text-left font-medium px-4 py-1.5">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {errors.slice(0, 100).map((e: BackfillCalibreMovementTypeError, i) => (
                  <tr key={`${e.calibreId}/${i}`}>
                    <td className="px-4 py-1.5 font-mono text-gray-700 align-top">{e.calibreId}</td>
                    <td className="px-4 py-1.5 text-red-600 align-top">{e.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }
}
